import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { html } from 'lit';

const CreateUserForm = stateful(
  (props: { onSubmit: (data: AdornisUser, sendInvite: boolean) => void; onCancel: () => void }) => {
    const [initialDocument] = useState(new AdornisUser({}));
    const [sendInvite, setSendInvite] = useState(true);
    const [emailError, setEmailError] = useState<string | null>(null);
    const { document, field, validate } = useForm<AdornisUser>(initialDocument, {
      validator: { username: nonOptional() },
    });

    return html`
      <div class="flex flex-col gap-4 min-w-0 w-full max-w-sm">
        <h3 class="text-lg font-bold">Create user</h3>
        <label class="form-control w-full">
          <div class="label"><span class="label-text">Username *</span></div>
          ${TextInput({ ...field('username'), placeholder: 'username' })}
        </label>
        <label class="form-control w-full">
          <div class="label"><span class="label-text">Email${sendInvite ? ' *' : ''}</span></div>
          ${TextInput({ ...field('email'), placeholder: 'user@example.com', type: 'email' })}
          ${emailError ? html`<p class="text-error text-sm mt-1">${emailError}</p>` : null}
        </label>
        <label class="label cursor-pointer justify-start gap-2">
          <input
            type="checkbox"
            class="checkbox checkbox-sm"
            .checked=${sendInvite}
            @change=${(e: Event) => {
              setSendInvite((e.target as HTMLInputElement).checked);
              setEmailError(null);
            }}
          />
          <span class="label-text">Send password setup email</span>
        </label>
        <div class="flex justify-end gap-2 mt-2 flex-wrap">
          <button class="btn btn-ghost" @click=${props.onCancel}>Cancel</button>
          <button
            class="btn btn-primary"
            @click=${async () => {
              const error = await validate();
              if (error) return;
              if (sendInvite && !document.email?.trim()) {
                setEmailError('Email is required to send the password setup email.');
                return;
              }
              props.onSubmit(document, sendInvite);
            }}
          >
            Create
          </button>
        </div>
      </div>
    `;
  },
);

export type CreateUserDialogResult = { userId: string; sendInvite: boolean } | null;

export async function showNexusCreateUserDialog(): Promise<CreateUserDialogResult> {
  const result = await showDialog<{ user: AdornisUser; sendInvite: boolean } | null>({
    content: (ok, cancel) =>
      CreateUserForm({
        onSubmit: (user, sendInvite) => ok({ user, sendInvite }),
        onCancel: () => cancel(),
      }),
  }).catch(() => null);

  if (!result?.user) return null;
  const newUserId = await result.user.create?.();
  if (!newUserId) return null;
  return { userId: newUserId, sendInvite: result.sendInvite };
}
